/*
 * TransferConferencePicker.jsx — Iter 185 (Feb 2026)
 *
 * Compact drop-up popover launched from the in-call "Transfer" and
 * "Conference" buttons. Searches internal users, presents the correct
 * action rows depending on `mode`.
 *
 * Props:
 *   mode        "transfer" | "conference"
 *   open        boolean
 *   onClose()   dismiss popover
 *   fromCallId  the active call session id (SIP Call-Id or RC session id)
 *   telephonySessionId? / partyId?    optional — enables cloud conference
 *   leadId?     optional — mirrors action into lead.activities
 *   onAction(action, target)   emits "transfer-blind" | "transfer-warm-start"
 *                              | "conference-add"; parent forwards to
 *                              RCWebPhone's webphone:control event.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Search, X, PhoneForwarded, Users, ArrowRight } from "lucide-react";

const authHdr = () => {
  const t = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function TransferConferencePicker({
  mode = "transfer",
  open,
  onClose,
  fromCallId,
  telephonySessionId,
  partyId,
  leadId,
  onAction,
}) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/integrations/rc/directory`, { headers: authHdr() })
      .then((r) => { if (!cancelled) setUsers(r?.data?.items || []); })
      .catch((e) => {
        console.warn("[picker] directory load failed", e?.response?.status);
        if (!cancelled) setUsers([]);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) => {
      const hay = `${u.full_name} ${u.email} ${u.extension} ${u.phone}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [users, q]);

  const doAction = async (action, target) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const body = {
        from_call_id: fromCallId,
        target_user_id: target.id,
        target_ext: target.extension || target.phone || "",
        target_name: target.full_name,
        lead_id: leadId || null,
      };
      if (action === "conference-add") {
        body.telephony_session_id = telephonySessionId || null;
        body.party_id = partyId || null;
        const r = await axios.post(`${API}/integrations/rc/calls/conference`, body, { headers: authHdr() });
        onAction?.(action, target, r?.data);
        if (r?.data?.mode === "cloud") {
          toast.success(`Bringing ${target.full_name} into the conference…`);
        } else {
          toast.info(`Cloud conference unavailable — using warm-transfer fallback.`);
        }
      } else {
        // Transfer — mode is derived from the action name
        body.mode = action === "transfer-blind" ? "blind" : "warm";
        const r = await axios.post(`${API}/integrations/rc/calls/transfer`, body, { headers: authHdr() });
        onAction?.(action, target, r?.data);
        toast.success(
          body.mode === "blind"
            ? `Transferring to ${target.full_name}…`
            : `Warm-transferring — talking to ${target.full_name}…`
        );
      }
      onClose?.();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      if (status === 403) {
        toast.error("You don't have permission for this action.");
      } else if (status === 404) {
        toast.error("This feature is currently disabled by your admin.");
      } else {
        toast.error(`${mode === "transfer" ? "Transfer" : "Conference"} failed: ${detail}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const isTransfer = mode === "transfer";
  const Icon = isTransfer ? PhoneForwarded : Users;
  return (
    <div
      data-testid={`${mode}-picker-popover`}
      style={{
        position: "absolute", bottom: 64, right: 8, width: 340,
        background: "#0F172A", color: "#E5E7EB",
        border: "1px solid #1F2937", borderRadius: 12,
        boxShadow: "0 20px 50px rgba(0,0,0,0.35)", zIndex: 60,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", borderBottom: "1px solid #1F2937" }}>
        <Icon size={16} color="#93C5FD" />
        <div style={{ fontSize: 13, fontWeight: 600 }}>
          {isTransfer ? "Transfer Call" : "Conference In"}
        </div>
        <button
          onClick={onClose}
          data-testid={`${mode}-picker-close`}
          aria-label="Close"
          style={{ marginLeft: "auto", background: "transparent", border: "none", color: "#94A3B8", cursor: "pointer" }}
        >
          <X size={16} />
        </button>
      </div>

      <div style={{ padding: "10px 12px" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 6,
          background: "#1E293B", border: "1px solid #334155", borderRadius: 8,
          padding: "6px 10px",
        }}>
          <Search size={14} color="#94A3B8" />
          <input
            data-testid={`${mode}-picker-search`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email or extension…"
            autoFocus
            style={{
              flex: 1, background: "transparent", border: "none", outline: "none",
              color: "#E5E7EB", fontSize: 12,
            }}
          />
        </div>
      </div>

      <div
        data-testid={`${mode}-picker-list`}
        style={{ maxHeight: 260, overflowY: "auto", padding: "0 6px 8px" }}
      >
        {loading && (
          <div style={{ padding: "16px", fontSize: 12, color: "#94A3B8", textAlign: "center" }}>
            Loading directory…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div style={{ padding: "16px", fontSize: 12, color: "#94A3B8", textAlign: "center" }}>
            No matching users.
          </div>
        )}
        {!loading && filtered.map((u) => (
          <div
            key={u.id}
            data-testid={`${mode}-picker-row-${u.id}`}
            style={{
              padding: "10px 10px",
              borderRadius: 8,
              marginBottom: 4,
              background: "#111827",
              border: "1px solid #1F2937",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {u.full_name || u.email}
                </div>
                <div style={{ fontSize: 10, color: "#94A3B8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {u.extension && <span>ext {u.extension}</span>}
                  {u.extension && u.email && <span> · </span>}
                  {u.email}
                </div>
              </div>
              <ArrowRight size={12} color="#64748B" />
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
              {isTransfer ? (
                <>
                  <button
                    data-testid={`transfer-warm-${u.id}`}
                    onClick={() => doAction("transfer-warm-start", u)}
                    disabled={submitting}
                    style={{
                      flex: 1, minWidth: 100,
                      background: "#2563EB", color: "#fff",
                      border: "none", borderRadius: 6, padding: "6px 10px",
                      fontSize: 11, fontWeight: 600, cursor: "pointer",
                    }}
                  >
                    Warm Transfer
                  </button>
                  <button
                    data-testid={`transfer-blind-${u.id}`}
                    onClick={() => doAction("transfer-blind", u)}
                    disabled={submitting}
                    style={{
                      flex: 1, minWidth: 100,
                      background: "#1F2937", color: "#E5E7EB",
                      border: "1px solid #334155", borderRadius: 6, padding: "6px 10px",
                      fontSize: 11, fontWeight: 500, cursor: "pointer",
                    }}
                  >
                    Blind Transfer
                  </button>
                </>
              ) : (
                <button
                  data-testid={`conference-add-${u.id}`}
                  onClick={() => doAction("conference-add", u)}
                  disabled={submitting}
                  style={{
                    flex: 1, minWidth: 120,
                    background: "#059669", color: "#fff",
                    border: "none", borderRadius: 6, padding: "6px 10px",
                    fontSize: 11, fontWeight: 600, cursor: "pointer",
                  }}
                >
                  Add to Conference
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
